import { useEffect, useState } from 'react'
import { getDocument } from '@/lib/firestore-crud'
import { Activity } from 'lucide-react'

export function PrivacyPolicyPublicPage() {
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadPolicy() {
      try {
        const doc = await getDocument<any>('systemConfig', 'privacyPolicy')
        if (doc) {
          setContent(doc.contentEn || doc.contentId || doc.content || 'Privacy policy is empty.')
        } else {
          setContent('Privacy policy not found.')
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load privacy policy.')
      } finally {
        setLoading(false)
      }
    }
    loadPolicy()
  }, [])

  if (loading) {
    return (
      <div className="min-h-[100dvh] h-full bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="flex flex-col items-center">
          <Activity className="h-8 w-8 text-orange-500 animate-pulse mb-4" />
          <p className="text-stone-400 font-medium">Loading Privacy Policy...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] h-full bg-[#0a0a0a] flex items-center justify-center p-4">
        <p className="text-rose-500">{error}</p>
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] h-full bg-[#0a0a0a] text-stone-300">
      <div className="max-w-3xl mx-auto bg-[#141414] min-h-[100dvh] border-x border-stone-800 shadow-2xl">
        <header className="sticky top-0 z-10 bg-[#141414]/90 backdrop-blur-md border-b border-stone-800 p-4 sm:p-6 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shrink-0 overflow-hidden outline outline-1 outline-white/10 outline-offset-[-1px]">
             <img src="/adaptive_foreground_icon.jpeg" alt="Logo" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">Parlent World</h1>
            <p className="text-xs text-stone-500 font-medium uppercase tracking-wider">Privacy Policy</p>
          </div>
        </header>

        <main className="p-6 sm:p-8">
          <div 
            className="prose prose-invert prose-orange max-w-none 
              prose-headings:text-white prose-headings:font-bold 
              prose-h1:text-3xl prose-h1:mt-12 prose-h1:mb-6 
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 
              prose-p:text-[15px] prose-p:leading-8 prose-p:mb-6 prose-p:text-stone-400
              prose-li:text-[15px] prose-li:leading-7 prose-li:mb-2 prose-li:text-stone-400
              prose-ul:mb-6
              prose-img:rounded-2xl prose-img:mt-8
              prose-a:text-orange-500 prose-a:no-underline hover:prose-a:text-orange-400
              [&>br]:content-[''] [&>br]:block [&>br]:mb-6"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </main>
        
        <footer className="p-6 border-t border-stone-800 text-center bg-stone-900/30">
           <p className="text-xs text-stone-500">© 2026 Parlent World. All rights reserved.</p>
        </footer>
      </div>
    </div>
  )
}
